import { withConsentPilot } from "../../utils/consent";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { buildPaymentAppUrl } from "../../config/paymentUrls";
import { appendSearchToUrl } from "../../utils/appendSearchToUrl";

const PAYWALL_URL = buildPaymentAppUrl("/hairstyle-3month-29-woman");
const SCRATCH_THRESHOLD = 0.25;
const AUTO_REVEAL_DELAY_MS = 6000;
const CONFETTI_DELAY_MS = 500;
const MODAL_DELAY_MS = 2000;
const CONFETTI_PIECES = Array.from({ length: 18 }, (_, index) => index);
const CONFETTI_COLORS = ["#7C3AED", "#22C55E", "#F59E0B", "#06B6D4", "#EC4899", "#8B5CF6"];

const defaultNavigateToPaywall = (url) => {
  window.location.assign(withConsentPilot(url));
};

const ScratchHairstylePage = ({ navigateToPaywall = defaultNavigateToPaywall }) => {
  const [isRevealed, setIsRevealed] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScratching, setIsScratching] = useState(false);
  const [showScratchHint, setShowScratchHint] = useState(true);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const autoRevealTimerRef = useRef(null);
  const confettiTimerRef = useRef(null);
  const modalTimerRef = useRef(null);
  const isRevealedRef = useRef(false);

  const paywallUrl = useMemo(() => appendSearchToUrl(PAYWALL_URL, window.location.search), []);

  const revealCard = () => {
    if (isRevealedRef.current) return;
    isRevealedRef.current = true;
    setIsRevealed(true);
    setIsScratching(false);
    setShowScratchHint(false);
    if (autoRevealTimerRef.current) {
      window.clearTimeout(autoRevealTimerRef.current);
      autoRevealTimerRef.current = null;
    }
    confettiTimerRef.current = window.setTimeout(() => {
      setShowConfetti(true);
    }, CONFETTI_DELAY_MS);
    modalTimerRef.current = window.setTimeout(() => {
      setIsModalOpen(true);
    }, MODAL_DELAY_MS);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return undefined;

    const { width: cssWidth, height: cssHeight } = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = cssWidth * dpr;
    canvas.height = cssHeight * dpr;
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;

    const ctx = canvas.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    const gradient = ctx.createLinearGradient(0, 0, cssWidth, cssHeight);
    gradient.addColorStop(0, "#E4BA8C");
    gradient.addColorStop(1, "#D9A56D");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, cssWidth, cssHeight);

    ctx.fillStyle = "rgba(255,255,255,0.92)";
    ctx.font = "700 18px Georgia, serif";
    ctx.textAlign = "center";
    ctx.fillText("Scratch", cssWidth / 2, cssHeight / 2 - 28);
    ctx.font = "600 14px Arial, sans-serif";
    ctx.fillText("to reveal your discount", cssWidth / 2, cssHeight / 2 - 2);

    ctx.globalCompositeOperation = "destination-out";

    return undefined;
  }, []);

  useEffect(() => {
    autoRevealTimerRef.current = window.setTimeout(() => {
      revealCard();
    }, AUTO_REVEAL_DELAY_MS);

    return () => {
      if (autoRevealTimerRef.current) {
        window.clearTimeout(autoRevealTimerRef.current);
      }
      if (confettiTimerRef.current) {
        window.clearTimeout(confettiTimerRef.current);
      }
      if (modalTimerRef.current) {
        window.clearTimeout(modalTimerRef.current);
      }
    };
  }, []);

  const handleGoToPaywall = () => {
    navigateToPaywall(paywallUrl);
  };

  const scratchAtPoint = (clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas || isRevealedRef.current) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / (rect.width * (window.devicePixelRatio || 1));
    const scaleY = canvas.height / (rect.height * (window.devicePixelRatio || 1));
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    const ctx = canvas.getContext("2d");

    ctx.beginPath();
    ctx.arc(x, y, 24, 0, Math.PI * 2);
    ctx.fill();

    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let transparentPixels = 0;
    for (let index = 3; index < pixels.length; index += 4) {
      if (pixels[index] === 0) transparentPixels += 1;
    }
    if (transparentPixels / (canvas.width * canvas.height) >= SCRATCH_THRESHOLD) {
      revealCard();
    }
  };

  const handlePointerDown = (event) => {
    if (isRevealed) return;
    setShowScratchHint(false);
    setIsScratching(true);
    scratchAtPoint(event.clientX, event.clientY);
  };

  const handlePointerMove = (event) => {
    if (!isScratching || isRevealed) return;
    scratchAtPoint(event.clientX, event.clientY);
  };

  const handlePointerUp = () => {
    setIsScratching(false);
  };

  return (
    <div className="min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top,#fff7ef_0%,#f5efe8_48%,#efe4d8_100%)] px-4 pb-10 pt-5 text-[#1F2937]">
      <div className="mx-auto w-full max-w-[420px]">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleGoToPaywall}
            className="text-[12px] font-medium uppercase tracking-[0.16em] text-[#6B7280] opacity-70"
          >
            Skip
          </button>
        </div>

        <div className="mt-4 text-center">
          <div className="mx-auto inline-flex items-center gap-1 rounded-full border border-[#EEDFD0] bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#A16207] shadow-sm">
            <span aria-hidden="true">🎁</span>
            <span>Claim your gift</span>
          </div>
          <h1 className="mt-7 text-[34px] font-semibold leading-[1.08] tracking-[-0.03em] text-[#1F2937]">
            Scratch to Unlock
            <span className="mt-1 block text-[#1F2937]">a Free Gift</span>
            <span className="mt-1 block text-[#7C3AED]">with Your Analysis</span>
          </h1>
        </div>

        <div className="relative mt-8 rounded-[28px] border border-[#D6E8D8] bg-[#F3FBF4] px-5 py-6 shadow-[0_20px_50px_rgba(162,181,140,0.18)]">
          <div className="absolute left-0 top-1/2 h-8 w-4 -translate-y-1/2 rounded-r-full bg-[#EFE4D8]" />
          <div className="absolute right-0 top-1/2 h-8 w-4 -translate-y-1/2 rounded-l-full bg-[#EFE4D8]" />
          <div className="transition-opacity duration-300 opacity-100">
            <p className="text-center text-[24px] font-semibold text-[#1F2937]">Your discount is:</p>
            <div className="mt-3 text-center text-[72px] font-semibold leading-none tracking-[-0.05em] text-[#7C3AED]">
              85%
            </div>
            <div className="mx-auto mt-4 h-px w-full max-w-[260px] border-t border-dashed border-[#C8D5C3]" />
            <p className="mx-auto mt-4 max-w-[240px] text-center text-[20px] leading-7 text-[#5B6472]">
              on premium features with instant try-on
            </p>
          </div>

          {!isRevealed && (
            <div
              ref={containerRef}
              className="absolute inset-0 overflow-hidden rounded-[28px]"
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
            >
              <canvas
                ref={canvasRef}
                onPointerDown={handlePointerDown}
                className="h-full w-full touch-none"
                aria-label="Scratch card"
              />
              {showScratchHint && (
                <>
                  <div className="pointer-events-none absolute inset-0">
                    <div className="absolute left-1/2 top-[62%] h-14 w-14 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/12 blur-xl animate-[scratchHintPulse_1400ms_ease-in-out_infinite]" />
                    <div className="absolute left-1/2 top-[62%] h-12 w-12 -translate-x-1/2 -translate-y-1/2 animate-[scratchHintMove_1900ms_ease-in-out_infinite]">
                      <div className="absolute inset-0 rounded-full border border-white/20 bg-white/10 shadow-[0_0_18px_rgba(255,255,255,0.18)] backdrop-blur-[2px]" />
                      <svg
                        viewBox="0 0 48 48"
                        className="absolute inset-0 h-full w-full"
                        aria-hidden="true"
                      >
                        <path
                          d="M26 10c-4 0-7 2.3-7 5.5 0 2.4 1.8 4.1 4.4 5.6 3 1.8 4.6 3.2 4.6 5.6 0 3-2.9 5.3-6.8 5.3-2.6 0-4.9-.8-6.3-2"
                          fill="none"
                          stroke="rgba(255,255,255,0.92)"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2.6"
                        />
                      </svg>
                    </div>
                  </div>
                  <style>
                    {`
                      @keyframes scratchHintMove {
                        0% { transform: translate(-88%, -54%) rotate(-12deg); }
                        22% { transform: translate(-28%, -44%) rotate(8deg); }
                        44% { transform: translate(-78%, -34%) rotate(-10deg); }
                        68% { transform: translate(-22%, -24%) rotate(9deg); }
                        100% { transform: translate(-88%, -54%) rotate(-12deg); }
                      }
                      @keyframes scratchHintPulse {
                        0%, 100% { opacity: 0.35; transform: translate(-50%, -50%) scale(0.92); }
                        50% { opacity: 0.75; transform: translate(-50%, -50%) scale(1.08); }
                      }
                    `}
                  </style>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {showConfetti && (
        <div className="pointer-events-none fixed inset-0 z-40 overflow-hidden">
          {CONFETTI_PIECES.map((piece) => (
            <span
              key={piece}
              className="absolute top-[-10%] block h-4 w-2 rounded-full opacity-90 animate-[fall_2200ms_ease-in_forwards]"
              style={{
                left: `${(piece * 91) % 100}%`,
                backgroundColor: CONFETTI_COLORS[piece % CONFETTI_COLORS.length],
                animationDelay: `${(piece % 6) * 80}ms`,
                transform: `rotate(${piece * 21}deg)`,
              }}
            />
          ))}
          <style>
            {`@keyframes fall { from { transform: translateY(0) rotate(0deg); } to { transform: translateY(120vh) rotate(360deg); } }`}
          </style>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-[#4B5563]/40 px-4 pb-6 pt-10 backdrop-blur-[2px] sm:items-center">
          <div className="w-full max-w-[360px] rounded-[28px] bg-white px-5 pb-4 pt-5 shadow-[0_24px_60px_rgba(31,41,55,0.22)]">
            <div className="rounded-[24px] bg-[linear-gradient(180deg,#ffffff_0%,#f8f7ff_100%)] px-4 pb-4 pt-3">
              <div className="text-center text-[30px]">🥳</div>
              <h2 className="mt-2 text-center text-[26px] font-semibold leading-none tracking-[-0.03em] text-[#374151]">
                Woohoo!
              </h2>
              <p className="mt-3 text-center text-[18px] text-[#6B7280]">Your discount is</p>
              <p className="mt-1 text-center text-[48px] font-semibold leading-none tracking-[-0.05em] text-[#6DA36C]">
                85% OFF
              </p>
              <button
                type="button"
                onClick={handleGoToPaywall}
                className="mt-5 inline-flex h-12 w-full items-center justify-center rounded-full bg-[#7C3AED] px-6 text-base font-semibold text-white shadow-[0_12px_24px_rgba(124,58,237,0.28)] transition-colors hover:bg-[#6D28D9] focus:outline-none focus:ring-2 focus:ring-[#7C3AED] focus:ring-offset-2"
              >
                Continue
              </button>
              <p className="mt-3 text-center text-[11px] leading-4 text-[#9CA3AF]">
                Your promo code will be applied automatically at the next step
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScratchHairstylePage;
